import { Col, Row, Space } from 'antd';
import dayjs from 'dayjs';
import { isEmpty, isNil } from 'lodash/fp';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useHref, useLinkClickHandler } from 'react-router-dom';
import styled from 'styled-components';
import { useThemeComponents } from '../../../../../themes';
import breakpoints from '../../../../../utilities/breakpoints';
import { CarSelectionContainer, Title } from '../../../StandardApplicationEntrypoint/CarListingPage/CarSelection/ui';
import { useCarListingState } from '../CarListingStateProvider';
import CarCard from './CarCard';

const StyledCarSelectionContainer = styled(CarSelectionContainer)<{ $openFilter?: boolean }>`
    margin-top: 8px;
    padding-right: ${({ $openFilter = false }) => ($openFilter ? '24px' : 0)};

    @media screen and (max-width: ${breakpoints.md}) {
        margin-top: 32px;
    }
`;

const StyledSpace = styled(Space)`
    margin-bottom: 1.5rem;
    line-height: 1;
`;

const StyledTitle = styled(Title)`
    &.ant-typography {
        margin-bottom: 0;
    }
`;

type AnchorLinkProps = {
    to: string;
    children?: React.ReactNode;
    icon?: React.ReactNode;
};

const AnchorLink = ({ to, children, icon }: AnchorLinkProps) => {
    const href = useHref(to);
    const { Anchor } = useThemeComponents();

    const onClick = useLinkClickHandler(to);

    return (
        <Anchor
            colorSet="primary"
            href={href}
            icon={icon}
            onClick={onClick}
            porscheFallbackIcon="arrow-right"
            variant="ghost"
        >
            {children}
        </Anchor>
    );
};

const CarSelectionContent = () => {
    const { t } = useTranslation(['carList', 'carDetails']);
    const {
        state: { stocks, openFilterDrawer, rentalPeriod, startTime, endTime },
        meta: { endpoint },
    } = useCarListingState();

    const [total] = useMemo(() => {
        const availableStocks = stocks.filter(stock => {
            if (
                stock.__typename !== 'MobilityStockInventory' ||
                stock.inventory.__typename !== 'MobilityInventory' ||
                stock.inventory.variant.__typename !== 'LocalVariant'
            ) {
                return [];
            }

            const from = rentalPeriod?.start ? dayjs(rentalPeriod?.start) : null;
            const to = rentalPeriod?.end ? dayjs(rentalPeriod?.end) : null;

            const start = startTime ? dayjs(startTime) : null;
            const end = endTime ? dayjs(endTime) : null;
            // from and/or to is not set, will not block the selection
            if (
                isNil(from) ||
                isNil(to) ||
                isNil(start) ||
                isNil(end) ||
                !from?.isValid() ||
                !to.isValid() ||
                !start.isValid() ||
                !end.isValid()
            ) {
                return true;
            }

            // check for reservations
            const hasReservation = stock.reservations.some(
                reservation =>
                    dayjs(reservation.bookingPeriod.start)
                        .subtract(endpoint.mobilityApplicationModule.durationBeforeNextBooking, 'hour')
                        .isBetween(from, to) ||
                    dayjs(reservation.bookingPeriod.end)
                        .add(endpoint.mobilityApplicationModule.durationBeforeNextBooking, 'hour')
                        .isBetween(from, to)
            );

            const hasBlockPeriod = stock.blockPeriod.some(block => {
                const updatedStartBlock = dayjs(block.start).subtract(
                    endpoint.mobilityApplicationModule.durationBeforeNextBooking,
                    'hour'
                );
                const updatedEnd = dayjs(block.end).add(
                    endpoint.mobilityApplicationModule.durationBeforeNextBooking,
                    'hour'
                );

                return (
                    dayjs(from).isBetween(updatedStartBlock, updatedEnd) ||
                    dayjs(to).isBetween(updatedStartBlock, updatedEnd)
                );
            });

            // check for available period, prefer settings on stocks over inventories
            const period = stock.period || stock.inventory.period;
            const isWithinAvailablePeriod =
                isEmpty(period) || (from.isSameOrAfter(period.start, 'day') && to.isSameOrBefore(period.end, 'day'));

            return !hasReservation && isWithinAvailablePeriod && !hasBlockPeriod;
        });

        return [availableStocks?.length];
    }, [
        endTime,
        endpoint.mobilityApplicationModule.durationBeforeNextBooking,
        rentalPeriod?.end,
        rentalPeriod?.start,
        startTime,
        stocks,
    ]);

    return (
        <StyledCarSelectionContainer $openFilter={openFilterDrawer}>
            <StyledSpace align="center" size={24}>
                <StyledTitle level={5}>{t('carList:noOfAvailable.vehicle', { count: total })}</StyledTitle>
                {endpoint.mobilityApplicationModule.giftVoucherModuleId && (
                    <AnchorLink to="./giftCode">{t('carDetails:actions.giftVoucher')}</AnchorLink>
                )}
            </StyledSpace>
            <Row gutter={[24, 40]}>
                {stocks.map(stock => (
                    <Col key={stock.id} sm={openFilterDrawer ? 24 : 12} xl={openFilterDrawer ? 8 : 6} xs={24}>
                        <CarCard stock={stock} />
                    </Col>
                ))}
            </Row>
        </StyledCarSelectionContainer>
    );
};

export default CarSelectionContent;
